import {useEffect} from 'react';

import {isMountedInShell} from '../shellRemote';
import {useSelector} from '../state/hooks';
import {useViewer} from '../state/viewer/hooks';
import {getViewerLoading} from '../state/viewer/selectors';
import {datadogEnabled, initDatadog} from './datadog/datadog';
import {startPerfObserver} from './datadog/perfObserver/performanceObserver';

/** How long to wait for the SaaS consent decision before giving up. */
const DATADOG_READY_TIMEOUT_MS = 5000;
const DATADOG_READY_POLL_MS = 100;

/** Consent may publish Datadog readiness after this effect runs, so poll briefly. This should only be relevant on saas. */
function initDatadogWhenReady(): () => void {
  if (datadogEnabled()) {
    initDatadog();
    return () => {};
  }

  const deadline = Date.now() + DATADOG_READY_TIMEOUT_MS;
  const poll = setInterval(() => {
    if (datadogEnabled()) {
      clearInterval(poll);
      initDatadog();
      return;
    }
    if (Date.now() >= deadline) {
      clearInterval(poll);
    }
  }, DATADOG_READY_POLL_MS);

  return () => clearInterval(poll);
}

export const useConsentAwareAnalyticsInjection = () => {
  const viewerIsLoading = useSelector(getViewerLoading);
  const viewer = useViewer();

  useEffect(() => {
    if (viewerIsLoading) {
      return;
    }

    let stopWaitingForDatadog = () => {};
    /*
      Both branches stay inside one try. Analytics setup must never take the app
      down with it: under the shell, `ShellErrorBoundary` in `shellMount.tsx`
      catches whatever this effect throws and renders the remote as `null`, so
      an `initDatadog` failure would blank the whole app inside Forge.
    */
    try {
      if (!isMountedInShell()) {
        // Despite its general legacy name, this injector is created only for SaaS.
        const injector = (window as any).WBAnalyticsInjector;
        if (injector) {
          injector.initializeTrackingScripts(!!viewer?.username);
        }
      }

      stopWaitingForDatadog = initDatadogWhenReady();
    } catch (e) {
      return;
    }

    // Outside the try/catch so dev-only contract validation errors stay visible.
    startPerfObserver();

    return stopWaitingForDatadog;
  }, [viewer, viewerIsLoading]);
};
